import { ApiError } from '@/lib/api'

/**
 * Retry policy for PUBLIC, shared-data reads (KAN-626).
 *
 * The regulatory read hooks carried no `retry` at all, so React Query
 * applied its default of 3 retries with exponential backoff. On
 * `GET /regulatory/compare` that was actively harmful: a 422 there means
 * "these two frameworks cannot form a valid pair", which is a permanent
 * fact about the request. Asking three more times cannot change it — it
 * just held the page on a skeleton for ~8.3s before the real message
 * appeared. KAN-627 restored Compare to the navigation, so this is a path
 * users reach.
 *
 * The rule:
 *   - A 4xx is the server telling us the request itself is wrong. Don't
 *     retry it — EXCEPT 408 (Request Timeout) and 429 (Too Many Requests),
 *     which are 4xx by number and transient by meaning.
 *   - A 5xx is a server-side blip. Retry.
 *   - A non-`ApiError` never reached a status: DNS, CORS, a dropped
 *     connection, a service-worker fault. Those do resolve on their own, so
 *     retry them too.
 *   - Cap transient retries at 2 (3 attempts total). Enough to ride out a
 *     redeploy; short of a retry storm.
 *
 * Contrast with `authGatedReadRetry` in `apps/web/src/hooks/queries.ts`,
 * which governs AUTH-GATED, user-scoped reads (`/preferences/:userId`).
 * That policy is deliberately stricter — it retries nothing but a single
 * 5xx — because a per-user read that fails is almost always a server bug
 * or an auth problem, and retrying amplified a 2026-04-30 smoke incident.
 * These regulatory endpoints are anonymous reads of shared data where a
 * transient retry genuinely helps, so the two policies differ on purpose.
 */
export const publicReadRetry = (
  failureCount: number,
  error: Error,
): boolean => {
  if (error instanceof ApiError) {
    const permanent =
      error.status >= 400 &&
      error.status < 500 &&
      error.status !== 408 &&
      error.status !== 429
    if (permanent) return false
  }
  return failureCount < 2
}
